import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const services = [
  {
    id: 1,
    label: 'Revisiones',
    subtitle: 'Mantenimiento preventivo',
    icon: 'ri-tools-line',
    image: 'https://public.readdy.ai/ai/img_res/a2799d66b4c3de03c2bf1bc9f4a46658.jpg',
    description: 'Revisiones completas siguiendo el plan del fabricante para mantener tu vehículo siempre en óptimas condiciones.',
    items: ['Cambio de aceite y filtros', 'Inspección de frenos', 'Diagnóstico completo'],
  },
  {
    id: 2,
    label: 'Diagnóstico',
    subtitle: 'Tecnología avanzada',
    icon: 'ri-search-eye-line',
    image: 'https://public.readdy.ai/ai/img_res/8ec4e3d3f1ed5803996d03eb8b1c7535.jpg',
    description: 'Diagnóstico electrónico avanzado con equipos de última generación para detectar cualquier fallo en tu vehículo.',
    items: ['Lectura de errores OBD', 'Análisis de sensores', 'Informe detallado'],
  },
  {
    id: 3,
    label: 'Reparación',
    subtitle: 'Mecánica y carrocería',
    icon: 'ri-settings-3-line',
    image: 'https://public.readdy.ai/ai/img_res/8eedd1e0a85a99e19820c77f1022c822.jpg',
    description: 'Reparaciones mecánicas y de carrocería con piezas originales y garantía total en todos los trabajos realizados.',
    items: ['Motor y transmisión', 'Carrocería y pintura', 'Garantía en piezas'],
  },
];

const ServicesSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [headerRef, headerInView] = useInView();
  const [contentRef, contentInView] = useInView(0.1);
  const active = services[activeIdx];

  return (
    <section className="bg-black py-20 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`flex items-end justify-between mb-10 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="w-5 h-px bg-yellow-400" />
              <span className="text-yellow-400 text-[10px] font-bold uppercase tracking-[0.3em]">Lo que hacemos</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl uppercase text-white leading-none tracking-wide">
              Nuestros <span className="text-yellow-400">Servicios</span>
            </h2>
          </div>
          <Link
            to="/servicios"
            className="hidden md:flex items-center gap-2 text-zinc-500 hover:text-yellow-400 text-xs font-bold uppercase tracking-widest transition-colors cursor-pointer whitespace-nowrap group"
          >
            Ver todos
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Mobile tabs */}
        <div className="flex gap-2 mb-4 lg:hidden overflow-x-auto pb-1 scrollbar-none">
          {services.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActiveIdx(i)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border text-xs font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                i === activeIdx
                  ? 'bg-yellow-400/10 border-yellow-400/40 text-yellow-400'
                  : 'bg-zinc-900/60 border-zinc-800 text-zinc-500 hover:border-zinc-600'
              }`}
            >
              <i className={`${s.icon} text-sm`} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Mobile content */}
        <div className="lg:hidden mb-4">
          <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
            <div className="relative aspect-[16/9]">
              <img alt={active.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" src={active.image} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-yellow-400 text-xs font-bold uppercase tracking-widest mb-0.5">{active.subtitle}</p>
                <h3 className="font-display text-2xl uppercase text-white">{active.label}</h3>
              </div>
            </div>
            <div className="p-5">
              <p className="text-zinc-400 text-sm leading-relaxed mb-3">{active.description}</p>
              <ul className="space-y-1.5 mb-4">
                {active.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
                    <i className="ri-checkbox-circle-line text-yellow-400 text-sm flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/contacto" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-yellow-400 cursor-pointer whitespace-nowrap">
                Pedir presupuesto <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop layout */}
        <div
          ref={contentRef}
          className={`hidden lg:grid grid-cols-5 gap-5 transition-all duration-700 ${
            contentInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="col-span-2 flex flex-col gap-3">
            {services.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left group flex items-center gap-4 p-4 border transition-all duration-300 cursor-pointer ${
                  i === activeIdx
                    ? 'bg-yellow-400/8 border-yellow-400/40'
                    : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900'
                }`}
              >
                <div className={`w-11 h-11 flex items-center justify-center flex-shrink-0 border transition-all duration-300 ${
                  i === activeIdx
                    ? 'bg-yellow-400 text-black border-yellow-400'
                    : 'bg-zinc-800/80 text-zinc-500 border-zinc-700 group-hover:text-yellow-400 group-hover:border-zinc-600'
                }`}>
                  <i className={`${s.icon} text-base`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold uppercase tracking-wide transition-colors ${
                    i === activeIdx ? 'text-white' : 'text-zinc-400 group-hover:text-white'
                  }`}>{s.label}</p>
                  <p className="text-xs text-zinc-600 mt-0.5 font-light">{s.subtitle}</p>
                </div>
                <span className={`text-xs font-black tracking-widest flex-shrink-0 font-display ${
                  i === activeIdx ? 'text-yellow-400' : 'text-zinc-800'
                }`}>
                  0{i + 1}
                </span>
              </button>
            ))}

            <div className="bg-zinc-900/50 border border-zinc-800 p-5 flex-1">
              <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-3">{active.subtitle}</p>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">{active.description}</p>
              <ul className="space-y-2 mb-5">
                {active.items.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <i className="ri-checkbox-circle-line text-yellow-400 text-sm flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-yellow-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap group/btn"
              >
                Pedir presupuesto
                <i className="ri-arrow-right-line group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          <div className="col-span-3 relative overflow-hidden border border-zinc-800 min-h-[420px]">
            {services.map((s, i) => (
              <img
                key={s.id}
                alt={s.label}
                className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                  i === activeIdx ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                }`}
                loading="lazy"
                src={s.image}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7 flex items-end justify-between">
              <div>
                <p className="text-yellow-400 text-xs font-bold uppercase tracking-[0.25em] mb-2">{active.subtitle}</p>
                <h3 className="font-display text-4xl uppercase text-white tracking-wide">{active.label}</h3>
              </div>
              <span className="font-display text-8xl text-white/8 select-none">0{activeIdx + 1}</span>
            </div>
          </div>
        </div>

        <div className="flex md:hidden justify-center mt-4">
          <Link to="/servicios" className="flex items-center gap-2 text-zinc-500 text-xs font-bold uppercase tracking-widest cursor-pointer">
            Ver todos los servicios <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
